import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ReactGA from 'react-ga4';

import { getEmailPreferences, updateEmailPreferences } from '../api/Api';
import GlowCard from './GlowCard';
import { useColors } from '../theme/ColorModeContext';

const DISMISSED_KEY = 'emailOptInDismissed';

interface EmailOptInPromptProps {
    token: string;
    // Where the prompt is shown, for GA comparison between surfaces.
    placement: string;
    headline?: string;
    body?: string;
    sx?: SxProps<Theme>;
}

/**
 * In-app invitation to opt in to product updates.
 *
 * This is how existing accounts join the list. They cannot be subscribed
 * retroactively (consent has to be a positive act by the user) and a
 * re-permission *email* to people who never opted in is the thing regulators
 * have actually fined companies for. So the ask lives in the product, framed as
 * a benefit and dismissible, in the same spirit as the Discord CTA.
 */
const EmailOptInPrompt = ({
    token,
    placement,
    headline = 'Know when new challenges drop',
    body = 'New challenges, new tracks, platform news. A couple of times a month at most, and one click to stop.',
    sx,
}: EmailOptInPromptProps) => {
    const colors = useColors();
    const [visible, setVisible] = useState(false);
    const [busy, setBusy] = useState(false);
    const [subscribed, setSubscribed] = useState(false);

    useEffect(() => {
        let dismissed = false;
        try {
            dismissed = localStorage.getItem(DISMISSED_KEY) === 'true';
        } catch {
            // private mode / blocked storage: just show the prompt
        }
        if (dismissed) return;

        const fetchPrefs = async () => {
            const prefs = await getEmailPreferences(token);
            // Only ever shown to someone who has made no choice yet: a user who
            // unsubscribed has answered, and must not be asked again here.
            if (prefs && !prefs.marketing_consent && !prefs.marketing_unsubscribed_at) {
                setVisible(true);
            }
        };
        fetchPrefs();
    }, [token]);

    const dismiss = () => {
        setVisible(false);
        try {
            localStorage.setItem(DISMISSED_KEY, 'true');
        } catch {
            // non-critical
        }
        try {
            ReactGA.event({ category: 'email_optin', action: 'dismiss', label: placement });
        } catch {
            // analytics must never break the interaction
        }
    };

    const subscribe = async () => {
        setBusy(true);
        const prefs = await updateEmailPreferences(token, true, 'in_app_prompt');
        setBusy(false);
        if (!prefs) return;

        setSubscribed(true);
        try {
            localStorage.setItem(DISMISSED_KEY, 'true');
        } catch {
            // non-critical
        }
        try {
            ReactGA.event({ category: 'email_optin', action: 'subscribe', label: placement });
        } catch {
            // analytics must never break the interaction
        }
    };

    if (!visible) return null;

    return (
        <GlowCard glowOnHover={false} sx={{ padding: '18px 20px', marginBottom: '24px', ...sx }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <MarkEmailReadIcon sx={{ color: colors.accent.cyan, marginTop: '2px' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {subscribed ? (
                        <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                            You are on the list. We will email you when new challenges and tracks land.
                            You can turn this off any time in Settings.
                        </Typography>
                    ) : (
                        <>
                            <Typography variant="body1" sx={{ color: colors.text.primary, fontWeight: 600 }}>
                                {headline}
                            </Typography>
                            <Typography variant="body2" sx={{ color: colors.text.secondary, marginTop: '3px' }}>
                                {body}
                            </Typography>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={subscribe}
                                disabled={busy}
                                sx={{ marginTop: '12px' }}
                            >
                                Email me updates
                            </Button>
                        </>
                    )}
                </Box>
                <IconButton size="small" onClick={dismiss} aria-label="Dismiss">
                    <CloseIcon sx={{ fontSize: '18px', color: colors.text.muted }} />
                </IconButton>
            </Box>
        </GlowCard>
    );
};

export default EmailOptInPrompt;
